import React from 'react';
import styled, { css } from 'styled-components';
import ArrowIcon, { Direction } from '@westwing/ui-kit/Icons/ArrowIcon';
import { forDesktop, forTablet } from '@westwing/ui-kit/breakPoints';
import { textLinesLimitEllipsisCss } from '@westwing/ui-kit/typography';
import Modal from '@westwing/ui-kit/Modal';
import ZoomInIcon from './assets/icons/zoomin.svg';
import Heading from '@ids/components/Typography/Heading';
import Text, { paragraphStyles, subtitleStyles } from '@ids/components/Typography/Text';
import Center from '@ids/components/Layout/Center';
import { Orientation } from '@ids/components/Media/MediaImage';
import { scrollMarginToSection } from '@ids/components/Layout/utils';

export const Title = styled(Heading)``;

export const Description = styled.h3`
    ${paragraphStyles}
    margin-bottom: 30px;

    ${forTablet`
        ${subtitleStyles}
    `}
`;

export const ButtonWrapper = styled(Center)`
    padding: 0 5vw;
`;

export const SlideImage = styled.div`
    position: relative;
    max-width: var(--slide-width);
    height: var(--slide-height);
    aspect-ratio: 16/9;
    @supports not (aspect-ratio: 16/9) {
        height: 0;
        padding-top: calc(100% * (9 / 16));
    }
    overflow: hidden;

    ${forDesktop`
        & > * {
            transition: all 150ms ease-out;
            transform: scale(1);
            transform-origin: center center;
        }

        &:hover > * {
            transform: scale(1.1);
        }
    `}
`;

export const SlideContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    cursor: url(${ZoomInIcon}) 20 10, zoom-in;
    min-width: var(--slide-width);
    min-height: calc(var(--slide-height) + 50px);
    margin-right: 5px;
    margin-left: 5px;
    scroll-snap-align: center;
    scroll-snap-stop: always;

    &:first-of-type {
        margin-left: 0;
    }
`;

export const SlideTitle = styled(Description)`
    margin-bottom: 0;
    margin-top: 0.5em;
`;

export const SlideText = styled(Text).attrs({
    as: 'p',
    variant: 'body',
})`
    ${textLinesLimitEllipsisCss(2)}
    margin-top: 5px;
`;

export const CarouselWrapper = styled.div`
    position: relative;
    margin-bottom: 50px;
    --slide-width: 300px;
    --slide-height: 200px;

    ${forTablet`
        --slide-width: 450px;
        --slide-height: 300px;
    `}

    ${forDesktop`
        --slide-width: 600px;
        --slide-height: 400px;
    `}
`;

export const ContentWrapper = styled.div``;

const ArrowButton = styled.button<{ size: 'string'; hide?: boolean }>`
    background: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.charcoal10};
    border-radius: 50%;
    cursor: pointer;
    width: ${p => p.size};
    height: ${p => p.size};
    display: none;
    ${forDesktop`
        display: flex;
        justify-content: center;
        align-items: center;
    `}
    visibility: ${p => (p.hide ? 'hidden' : 'visible')};
`;

export const LeftArrow = styled(ArrowButton).attrs({
    children: <ArrowIcon direction={Direction.Left} size={14} />,
    size: '40px',
})`
    position: absolute;
    top: calc(var(--slide-height, 200px) / 2 - 20px);
    left: 1em;
    padding-left: 0.5em;
`;

export const RightArrow = styled(ArrowButton).attrs({
    children: <ArrowIcon direction={Direction.Right} size={14} />,
    size: '40px',
})`
    position: absolute;
    top: calc(var(--slide-height, 200px) / 2 - 20px);
    right: 1em;
    padding-right: 0.5em;
`;

export const ModalOverlay = styled(Modal)`
    && {
        background: rgba(29, 29, 31, 0.3);
        display: flex;
        align-items: center;
    }
`;

export const ModalContent = styled(Modal.Content)<{ $orientation?: Orientation }>`
    position: relative;
    background: transparent;
    margin: auto;
    width: 100vw;
    max-height: 100vh;

    @media (orientation: landscape) {
        ${({ $orientation }) => {
            if ($orientation === 'LANDSCAPE') {
                return css`
                    width: 75vw;
                    height: auto;
                `;
            }
            return css`
                width: auto;
                height: 90vh;
            `;
        }}
    }
`;

export const ModalCloseButton = styled(Modal.CloseButton)`
    color: ${({ theme }) => theme.colors.white};
    padding: 6px 12px;
    filter: drop-shadow(0 0 0.5rem ${({ theme }) => theme.colors.charcoal100});
    outline: none;
`;

export const SectionContainer = styled.section`
    padding: 10px min(5vw, 40px);

    ${scrollMarginToSection}
`;

const modalArrowButtonStyles = css`
    z-index: 10;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0.9;
    display: block;

    & > span {
        width: 10px;
        height: 10px;
    }

    ${forDesktop`
        display: inherit;

        & > span {
            width: 14px;
            height: 14px;
        }
    `}
`;

export const RightArrowModal = styled(RightArrow)`
    ${modalArrowButtonStyles}

    padding-right: 0.25em;

    ${forDesktop`
        padding-right: 0.5em;
    `}
`;

export const LeftArrowModal = styled(LeftArrow)`
    ${modalArrowButtonStyles}

    padding-left: 0.25em;

    ${forDesktop`
        padding-left: 0.5em;
    `}
`;
